import { z } from 'zod';

import { axiosRubyClient } from '@/util/axios/axiosClient';

// GRAVY-10898 — whether "Auto backups" run, per client, order and job: GravyWork
// inviting backups itself at the 3-hour confirmation check, standby, and
// hiring the earliest standby when a spot opens. Shared by the client modal, the Order and Job pages and Dashboard V2's
// Invite backups modal, like PaySurge's client is.
//
// What actually runs for a job is: the GLOBAL switch is on AND
// (job ?? order ?? client). `null` at the job or order level means "inherit",
// so the order and job writes take `null` to clear an override, and a client
// is always explicit. Clients are OPT-IN: an unconfigured client is off, and a
// client value the API leaves out is read as off (`clientFlag`), never on. The screens derive the shown value
// and its source from these fields with `levelValue` below rather than from
// `effective`, which also folds in the global switch: a job set ON while the
// switch is off must still read "On" at its own level.
//
// Every endpoint is TENANT_ADMIN-role only; helpdesk and client admins get 403.

export const backupLevel = z.enum(['job', 'order', 'client']);
export type BackupLevel = z.infer<typeof backupLevel>;

const id = z.number().int();
const count = z.number().int().nonnegative();
// A client's setting; missing or null means it was never turned on.
const clientFlag = z
  .boolean()
  .nullish()
  .transform((v) => v === true);

export const customerBackupsSchema = z
  .object({
    customer_id: id,
    enabled: clientFlag,
    switch_on: z.boolean(),
  })
  .passthrough();

const orderJobBackupsSchema = z
  .object({
    job_id: id,
    enabled: z.boolean().nullable(),
    effective: z.boolean(),
    source: backupLevel,
    on_standby_count: count,
    invited_count: count,
  })
  .passthrough();

export const orderBackupsSchema = z
  .object({
    order_id: id,
    enabled: z.boolean().nullable(),
    client_enabled: clientFlag,
    effective: z.boolean(),
    source: backupLevel,
    switch_on: z.boolean(),
    jobs: z.array(orderJobBackupsSchema),
  })
  .passthrough();

export const jobBackupSettingSchema = z
  .object({
    job_id: id,
    enabled: z.boolean().nullable(),
    order_enabled: z.boolean().nullable(),
    client_enabled: clientFlag,
    effective: z.boolean(),
    source: backupLevel,
    switch_on: z.boolean(),
    on_standby_count: count,
    invited_count: count,
  })
  .passthrough();

export type CustomerBackups = z.infer<typeof customerBackupsSchema>;
export type OrderBackups = z.infer<typeof orderBackupsSchema>;
export type OrderJobBackups = z.infer<typeof orderJobBackupsSchema>;
export type JobBackupSetting = z.infer<typeof jobBackupSettingSchema>;

// The customer endpoint sends `success: true`; the order and job ones are
// specified with `data` alone, so `success` is not required here.
const envelope = <T extends z.ZodTypeAny>(data: T) =>
  z.object({ data }).passthrough();

const customerPath = (customerId: number | string) =>
  `/api/admin/customers/${customerId}/backup_invites`;
const orderPath = (orderId: number | string) =>
  `/api/admin/orders/${orderId}/backup_invites`;
const jobPath = (jobId: number | string) =>
  `/api/admin/jobs/${jobId}/backup_invites/setting`;

export const backupSettingsClient = {
  async getCustomer(customerId: number | string): Promise<CustomerBackups> {
    const { data } = await axiosRubyClient.get(customerPath(customerId));
    return envelope(customerBackupsSchema).parse(data).data;
  },

  // A client is always explicit: there is nothing above it to inherit from.
  async setCustomer(
    customerId: number | string,
    enabled: boolean,
  ): Promise<CustomerBackups> {
    const { data } = await axiosRubyClient.patch(customerPath(customerId), {
      enabled,
    });
    return envelope(customerBackupsSchema).parse(data).data;
  },

  async getOrder(orderId: number | string): Promise<OrderBackups> {
    const { data } = await axiosRubyClient.get(orderPath(orderId));
    return envelope(orderBackupsSchema).parse(data).data;
  },

  /** `null` clears the order's override: it follows the client again. */
  async setOrder(
    orderId: number | string,
    enabled: boolean | null,
  ): Promise<OrderBackups> {
    const { data } = await axiosRubyClient.patch(orderPath(orderId), {
      enabled,
    });
    return envelope(orderBackupsSchema).parse(data).data;
  },

  async getJob(jobId: number | string): Promise<JobBackupSetting> {
    const { data } = await axiosRubyClient.get(jobPath(jobId));
    return envelope(jobBackupSettingSchema).parse(data).data;
  },

  /** `null` clears the job's override: it follows the order again. */
  async setJob(
    jobId: number | string,
    enabled: boolean | null,
  ): Promise<JobBackupSetting> {
    const { data } = await axiosRubyClient.patch(jobPath(jobId), { enabled });
    return envelope(jobBackupSettingSchema).parse(data).data;
  },
};

/**
 * The value a level shows and where it comes from: its own override, else the
 * first level above it that has one. `inherited` is what clearing the
 * override would give, i.e. what "Use order/client setting" switches to.
 */
export const levelValue = (
  level: BackupLevel,
  {
    job = null,
    order = null,
    client,
  }: { job?: boolean | null; order?: boolean | null; client: boolean },
): { value: boolean; source: BackupLevel; inherited: boolean | null } => {
  if (level === 'client') {
    return { value: client, source: 'client', inherited: null };
  }
  const inherited = level === 'job' ? (order ?? client) : client;
  const own = level === 'job' ? job : order;
  if (own != null) return { value: own, source: level, inherited };
  const source: BackupLevel =
    level === 'job' && order != null ? 'order' : 'client';
  return { value: inherited, source, inherited };
};
